import { ElectronicHousingUnitApi } from '../../../services/Api/ElectronicHousingUnitApi';
import { PartsApi } from '../../../services/Api/PartsApi';
import { flattenRow } from '../exportUtils';

const EHU_SHEET_NAME = 'Electronic Housing Units';

const partsSheetColumns = [
  { header: 'EHU Serial Number', getValue: row => row._ehuProductCode },
  { header: 'Serial Number', getValue: row => row.serialNumber },
  { header: 'Type', getValue: row => row['specification']['PartType'] },
  { header: 'Supplier', getValue: row => row.supplier },
  { header: 'Order Number', getValue: row => row.orderNumber },
  { header: 'Order Date', getValue: row => row.orderDate },
  { header: 'Shipping Date', getValue: row => row.shippingDate },
  { header: 'Status', getValue: row => row.reference },
];

export const ehuExportConfig = {
  entityName: 'Electronic Housing Units',
  filename: 'electronic-housing-units',
  sheetName: EHU_SHEET_NAME,
  companyFields: [
    { field: 'customerId', strategy: 'numericId' },
    { field: 'assemblyCompanyId', strategy: 'numericId' },
    { field: 'installationCompanyId', strategy: 'numericId' },
  ],
  columns: [
    { header: 'Serial Number', getValue: row => row.productCode },
    { header: 'Certificate', getValue: row => row.certificate },
    { header: 'Owner', getValue: row => row.customerId },
    { header: 'Product Type', getValue: row => row.productType },
    { header: 'Status', getValue: row => row.status },
    { header: 'Assembly Partner', getValue: row => row.assemblyCompanyId },
    { header: 'Installation Partner', getValue: row => row.installationCompanyId },
  ],
  filters: [
    { label: 'Product Type', field: 'productType', type: 'multi-select' },
    { label: 'Status', field: 'status', type: 'multi-select' },
    { label: 'Owner', field: 'customerId', type: 'multi-select', companyOptions: { valueKey: 'numericId' } },
    { label: 'Assembly Partner', field: 'assemblyCompanyId', type: 'multi-select', companyOptions: { valueKey: 'numericId' } },
    { label: 'Installation Partner', field: 'installationCompanyId', type: 'multi-select', companyOptions: { valueKey: 'numericId' } },
  ],
  fetchExtraSheets: async (filteredEhus) => {
    // Fetch EHU-linked part IDs and all full parts in parallel.
    // EHU endpoint returns parts without specification join, so we enrich
    // each part with the full record from the Parts endpoint.
    const [partsLists, allPartsResponse] = await Promise.all([
      Promise.all(
        filteredEhus.map(ehu =>
          ElectronicHousingUnitApi.getElectronicHousingUnitParts(ehu.id)
            .then(res => res.data || [])
            .catch(() => [])
        )
      ),
      PartsApi.getParts().then(res => res.data || []).catch(() => []),
    ]);

    const fullPartsById = new Map(allPartsResponse.map(p => [p.id, p]));

    // Build lookup: EHU productCode → Excel row number in Sheet 1 (1-indexed + header row)
    const ehuSerialToSheetRow = Object.fromEntries(
      filteredEhus.map((ehu, i) => [ehu.productCode, i + 2])
    );

    const headers = partsSheetColumns.map(c => c.header);
    const rows = filteredEhus.flatMap((ehu, i) =>
      (partsLists[i] || []).map(part => {
        const fullPart = fullPartsById.get(part.id) || part;
        return flattenRow({ ...fullPart, _ehuProductCode: ehu.productCode }, partsSheetColumns);
      })
    );

    return [{ name: 'Parts', headers, rows, ehuSerialToSheetRow, ehuSheetName: EHU_SHEET_NAME }];
  },
};
